import type { SelectedPlan } from "@/shared/types/user";
import type { PlanDetails, PlansCatalog } from "../types";

/** Ordine dei piani nella tabella di BillingPlans: serve a distinguere i piani inferiori. */
export const PLAN_KEYS = ["little", "start", "standard", "advanced", "enterprise"] as const;

export const TRIAL_PLAN = { trial: { key: "trial" } };

const DAY_MS = 86_400_000;
/** Consumo fisso del legacy (`var totalMonthly = 4000`): quirk replicato. */
const FIXED_MONTHLY_QUERIES = 4000;

/** Il backend risponde con un array vuoto quando non c'e' un piano: equivale al trial. */
export function normalizeSelectedPlan(response: SelectedPlan | unknown[]): SelectedPlan {
  return Array.isArray(response) && response.length === 0 ? TRIAL_PLAN : (response as SelectedPlan);
}

export function planEntry(selected: SelectedPlan): { key: string; plan: PlanDetails } {
  const key = Object.keys(selected)[0] ?? "";
  return { key, plan: (Object.values(selected)[0] ?? {}) as PlanDetails };
}

/** Il piano in attesa, se c'e', prevale su quello corrente. */
export function effectivePlanKey(key: string, plan: PlanDetails): string {
  return plan.pendingPlan ? plan.pendingPlan.planKey : key;
}

export function effectiveIsMonthly(plan: PlanDetails): boolean | undefined {
  return plan.pendingPlan ? plan.pendingPlan.isMonthly : plan.isMonthly;
}

/** `disabled` legacy: piani sotto quello corrente, ed enterprise sempre. */
export function isBelowCurrentPlan(planKey: string, currentKey: string): boolean {
  const order = PLAN_KEYS as readonly string[];
  return order.indexOf(planKey) < order.indexOf(currentKey) || planKey === "enterprise";
}

export function isTrialPeriod(key: string, plan: PlanDetails, now: number): boolean {
  return key === "trial" || now < (plan.subscriptionTimestamp as number) * 1000;
}

export function queryThreshold(key: string, plan: PlanDetails, now: number): number {
  const value = isTrialPeriod(key, plan, now)
    ? 1000
    : // Come il legacy (`requestsLimit == 0`): anche "0" e "" ricadono su queries.threshold.
      (plan.requestsLimit as unknown) == 0
      ? plan.queries?.threshold
      : plan.requestsLimit;
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * calcRecommended del legacy, quirk compresi: il consumo e' fisso a 4000 query e vince il piano
 * con la soglia piu' alta tra quelli sopra la previsione.
 */
export function recommendedPlan(
  plans: PlansCatalog,
  periodStartTimestamp: number | undefined,
  now: number,
): string | undefined {
  const daysPassed = (now - (periodStartTimestamp ?? NaN) * 1000) / DAY_MS;
  const forecast = (FIXED_MONTHLY_QUERIES / daysPassed) * 30;
  const thresholdOf = (plan: PlanDetails) => plan.queries?.threshold as number;
  return Object.values(plans)
    .filter((plan) => thresholdOf(plan) > forecast)
    .sort((a, b) => thresholdOf(b) - thresholdOf(a))[0]?.key;
}
